/**
 * PÅRØRENDE OG FØDSELSDATO ER ÉN PLATTFORMREGEL (PERSONTYPER-BESTILLING §1, migrering 1077).
 *
 * Hvilke roller som ser de sensitive feltene, er en innstilling i Control Room for hele plattformen,
 * ikke en celle per organisasjon: den laveste rollen som ser dem. Standard er medarbeider. Begrenset
 * kan velges (283.2, 1150), men er aldri standarden.
 *
 * Basen svarer med `app.sees_sensitive_person_fields()`; `roleSeesSensitivePersonFields` er den samme
 * regelen for den som alt kjenner rollen og grensen (tester og skjermtekst).
 */
import { z } from 'zod';

import { wireInstant } from './wire-time.js';

export const SENSITIVE_FIELDS_MIN_ROLES = ['limited', 'member', 'admin', 'superadmin'] as const;
export type SensitiveFieldsMinRole = (typeof SENSITIVE_FIELDS_MIN_ROLES)[number];
export const DEFAULT_SENSITIVE_FIELDS_MIN_ROLE: SensitiveFieldsMinRole = 'member';

/** Rollen i huset er minst grensen. Ekstern, gjest og ingen rolle er alltid nei. */
export function roleSeesSensitivePersonFields(
  role: string | null | undefined,
  minRole: SensitiveFieldsMinRole,
): boolean {
  const rank = (SENSITIVE_FIELDS_MIN_ROLES as readonly string[]).indexOf(role ?? '');
  return rank >= 0 && rank >= SENSITIVE_FIELDS_MIN_ROLES.indexOf(minRole);
}

/** `GET /v1/platform/sensitive-fields` — Control Room › Standarder. */
export const platformSensitiveFieldsSettingSchema = z
  .object({
    minRole: z.enum(SENSITIVE_FIELDS_MIN_ROLES),
    /** Null til grensen er endret første gang. */
    updatedAt: wireInstant.nullable(),
  })
  .meta({ id: 'PlatformSensitiveFieldsSetting' });
export type PlatformSensitiveFieldsSetting = z.infer<typeof platformSensitiveFieldsSettingSchema>;

/** `PUT /v1/platform/sensitive-fields`. */
export const updatePlatformSensitiveFieldsSettingRequestSchema = z
  .strictObject({ minRole: z.enum(SENSITIVE_FIELDS_MIN_ROLES) })
  .meta({ id: 'UpdatePlatformSensitiveFieldsSettingRequest' });
export type UpdatePlatformSensitiveFieldsSettingRequest = z.infer<
  typeof updatePlatformSensitiveFieldsSettingRequestSchema
>;
